import { 
  query, 
  collection, 
  getDocs, 
  where, 
  limit, 
  orderBy, 
  doc, 
  updateDoc, 
  getDoc, 
  setDoc, 
  addDoc,
  deleteDoc,
  writeBatch,
  runTransaction,
  onSnapshot,
  startAfter,
  endBefore,
  queryEqual,
  QuerySnapshot,
  getFirestore
} from "firebase/firestore";
import { initializeApp, getApps, getApp } from 'firebase/app';
import { UserStatus, UserRole } from '../enums/auth';

// Firebase 설정
const firebaseConfig = {
  apiKey: process.env.REACT_APP_API_KEY,
  authDomain: process.env.REACT_APP_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_PROJECT_ID,
  storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
  databaseURL: process.env.REACT_APP_DATABASE_URL,
  messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_APP_ID,
  measurementId: process.env.REACT_APP_MEASUREMENT_ID
};

// Firebase 앱 초기화 (중복 방지)
const app = getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);
const db = getFirestore(app);

class FirestoreService {
  constructor() {
    this.db = db;
  }

  // 기본 문서 읽기
  async getDocument(collectionName, documentId) {
    try {
      const docRef = doc(this.db, collectionName, documentId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          success: true,
          data: { id: docSnap.id, ...docSnap.data() },
          error: null
        };
      } else {
        return {
          success: false,
          data: null,
          error: "문서를 찾을 수 없습니다."
        };
      }
    } catch (error) {
      return {
        success: false,
        data: null,
        error: error.message
      };
    }
  }

  // 컬렉션 전체 읽기
  async getCollection(collectionName) {
    try {
      const q = query(collection(this.db, collectionName));
      const querySnapshot = await getDocs(q);
      
      const documents = [];
      querySnapshot.forEach((doc) => {
        documents.push({ id: doc.id, ...doc.data() });
      });
      
      return {
        success: true,
        data: documents,
        error: null
      };
    } catch (error) {
      return {
        success: false,
        data: null,
        error: error.message
      };
    }
  }

  // 정렬된 컬렉션 읽기
  async getCollectionWithSort(collectionName, sortField, sortDirection = 'asc') {
    try {
      const q = query(
        collection(this.db, collectionName), 
        orderBy(sortField, sortDirection)
      );
      const querySnapshot = await getDocs(q);
      
      const documents = [];
      querySnapshot.forEach((doc) => {
        documents.push({ id: doc.id, ...doc.data() });
      });
      
      return {
        success: true,
        data: documents,
        error: null
      };
    } catch (error) {
      return {
        success: false,
        data: null,
        error: error.message
      };
    }
  }

  // 조건부 쿼리
  async getCollectionWithQuery(collectionName, queries) {
    try {
      const whereQueries = Array.isArray(queries) 
        ? queries.map(q => where(q.field, q.operator, q.value))
        : [where(queries.field, queries.operator, queries.value)];

      const q = query(collection(this.db, collectionName), ...whereQueries);
      const querySnapshot = await getDocs(q);
      
      const documents = [];
      querySnapshot.forEach((doc) => {
        documents.push({ id: doc.id, ...doc.data() });
      });
      
      return {
        success: true,
        data: documents,
        error: null
      };
    } catch (error) {
      return {
        success: false,
        data: null,
        error: error.message
      };
    }
  }

  // 조건부 쿼리 + 정렬
  async getCollectionWithQueryAndSort(collectionName, queries, sortField, sortDirection = 'asc') {
    try {
      const whereQueries = Array.isArray(queries) 
        ? queries.map(q => where(q.field, q.operator, q.value))
        : [where(queries.field, queries.operator, queries.value)];

      const q = query(
        collection(this.db, collectionName), 
        ...whereQueries,
        orderBy(sortField, sortDirection)
      );
      const querySnapshot = await getDocs(q);
      
      const documents = [];
      querySnapshot.forEach((doc) => {
        documents.push({ id: doc.id, ...doc.data() });
      });
      
      return {
        success: true,
        data: documents,
        error: null
      };
    } catch (error) {
      return {
        success: false,
        data: null,
        error: error.message
      };
    }
  }

  // 페이지네이션 쿼리
  async getCollectionWithPagination(collectionName, pageSize = 10, lastDoc = null, sortField = 'createdAt', sortDirection = 'desc') {
    try {
      let q = query(
        collection(this.db, collectionName),
        orderBy(sortField, sortDirection),
        limit(pageSize)
      );

      if (lastDoc) {
        q = query(
          collection(this.db, collectionName),
          orderBy(sortField, sortDirection),
          startAfter(lastDoc),
          limit(pageSize)
        );
      }

      const querySnapshot = await getDocs(q);
      
      const documents = [];
      querySnapshot.forEach((doc) => {
        documents.push({ id: doc.id, ...doc.data() });
      });
      
      const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];
      
      return {
        success: true,
        data: documents,
        lastDoc: lastVisible,
        hasMore: querySnapshot.docs.length === pageSize,
        error: null
      };
    } catch (error) {
      return {
        success: false,
        data: null,
        lastDoc: null,
        hasMore: false,
        error: error.message
      };
    }
  }

  // 범위 쿼리
  async getCollectionWithRange(collectionName, startField, startValue, endField, endValue) {
    try {
      const q = query(
        collection(this.db, collectionName),
        where(startField, '>=', startValue),
        where(endField, '<=', endValue)
      );
      const querySnapshot = await getDocs(q);
      
      const documents = [];
      querySnapshot.forEach((doc) => {
        documents.push({ id: doc.id, ...doc.data() });
      });
      
      return {
        success: true,
        data: documents,
        error: null
      };
    } catch (error) {
      return {
        success: false,
        data: null,
        error: error.message
      };
    }
  }

  // 문서 생성
  async createDocument(collectionName, data, documentId = null) {
    try {
      let docRef;
      
      if (documentId) {
        // 특정 ID로 문서 생성
        docRef = doc(this.db, collectionName, documentId);
        await setDoc(docRef, {
          ...data,
          registered_at: new Date(),
          updated_at: new Date()
        });
      } else {
        // 자동 ID로 문서 생성
        docRef = await addDoc(collection(this.db, collectionName), {
          ...data,
          registered_at: new Date(),
          updated_at: new Date()
        });
      }
      
      return {
        success: true,
        documentId: docRef.id,
        error: null
      };
    } catch (error) {
      return {
        success: false,
        documentId: null,
        error: error.message
      };
    }
  }

  // 문서 업데이트
  async updateDocument(collectionName, documentId, data) {
    try {
      const docRef = doc(this.db, collectionName, documentId);
      await updateDoc(docRef, {
        ...data,
        updated_at: new Date()
      });
      
      return {
        success: true,
        error: null
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  // 문서 삭제
  async deleteDocument(collectionName, documentId) {
    try {
      const docRef = doc(this.db, collectionName, documentId);
      await deleteDoc(docRef);
      
      return {
        success: true,
        error: null
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  // 배치 작업
  async batchWrite(operations) {
    try {
      const batch = writeBatch(this.db);
      
      operations.forEach(operation => {
        const { type, collectionName, documentId, data } = operation;
        const docRef = doc(this.db, collectionName, documentId);
        
        switch (type) {
          case 'set':
            batch.set(docRef, data);
            break;
          case 'update':
            batch.update(docRef, data);
            break;
          case 'delete':
            batch.delete(docRef);
            break;
          default:
            throw new Error(`알 수 없는 작업 유형: ${type}`);
        }
      });
      
      await batch.commit();
      
      return {
        success: true,
        error: null
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  // 트랜잭션
  async runTransaction(updateFunction) {
    try {
      const result = await runTransaction(this.db, updateFunction);
      return {
        success: true,
        data: result,
        error: null
      };
    } catch (error) {
      return {
        success: false,
        data: null,
        error: error.message
      };
    }
  }

  // 실시간 리스너
  subscribeToCollection(collectionName, callback, queries = [], sortField = null, sortDirection = 'asc') {
    try {
      let q = query(collection(this.db, collectionName));
      
      if (queries.length > 0) {
        const whereQueries = queries.map(q => where(q.field, q.operator, q.value));
        q = query(collection(this.db, collectionName), ...whereQueries);
      }
      
      if (sortField) {
        q = query(q, orderBy(sortField, sortDirection));
      }
      
      return onSnapshot(q, (querySnapshot) => {
        const documents = [];
        querySnapshot.forEach((doc) => {
          documents.push({ id: doc.id, ...doc.data() });
        });
        callback({
          success: true,
          data: documents,
          error: null
        });
      }, (error) => {
        callback({
          success: false,
          data: null,
          error: error.message
        });
      });
    } catch (error) {
      callback({
        success: false,
        data: null,
        error: error.message
      });
      return null;
    }
  }

  // 실시간 문서 리스너
  subscribeToDocument(collectionName, documentId, callback) {
    try {
      const docRef = doc(this.db, collectionName, documentId);
      
      return onSnapshot(docRef, (docSnap) => {
        if (docSnap.exists()) {
          callback({
            success: true,
            data: { id: docSnap.id, ...docSnap.data() },
            error: null
          });
        } else {
          callback({
            success: false,
            data: null,
            error: "문서를 찾을 수 없습니다."
          });
        }
      }, (error) => {
        callback({
          success: false,
          data: null,
          error: error.message
        });
      });
    } catch (error) {
      callback({
        success: false,
        data: null,
        error: error.message
      });
      return null;
    }
  }

  // 검색 기능 (간단한 구현)
  async searchDocuments(collectionName, searchField, searchTerm) {
    try {
      // Firestore는 전체 텍스트 검색을 지원하지 않으므로
      // 정확한 일치나 prefix 검색만 가능
      const q = query(
        collection(this.db, collectionName),
        where(searchField, '>=', searchTerm),
        where(searchField, '<=', searchTerm + '\uf8ff')
      );
      
      const querySnapshot = await getDocs(q);
      
      const documents = [];
      querySnapshot.forEach((doc) => {
        documents.push({ id: doc.id, ...doc.data() });
      });
      
      return {
        success: true,
        data: documents,
        error: null
      };
    } catch (error) {
      return {
        success: false,
        data: null,
        error: error.message
      };
    }
  }
}

// 싱글톤 인스턴스 생성
const firestoreService = new FirestoreService();

export default firestoreService; 